import * as React from "react";
import type { NotificationSeverityName } from "@/lib/tokens";

export type ToastTone = NotificationSeverityName;

export interface ToastActionConfig {
  label: string;
  onClick: () => void;
  altText?: string;
}

export interface ToastOptions {
  id?: string;
  tone?: ToastTone;
  title?: React.ReactNode;
  description?: React.ReactNode;
  icon?: React.ReactNode | null;
  action?: ToastActionConfig;
  dismissible?: boolean;
  duration?: number | null;
  progress?: number | null;
  sound?: boolean;
  onDismiss?: () => void;
  onOpenChange?: (open: boolean) => void;
}

interface ToastInternal extends ToastOptions {
  id: string;
  tone: ToastTone;
  open: boolean;
  createdAt: number;
  dismissible: boolean;
  durationMs: number;
  autoDismiss: boolean;
  progress: number | null;
}

interface ToastState {
  toasts: ToastInternal[];
}

const TOAST_STACK_LIMIT = 3;
const TOAST_REMOVE_DELAY = 200;
const PERSIST_DURATION = Number.MAX_SAFE_INTEGER;

const DEFAULT_DURATIONS: Record<ToastTone, number | null> = {
  info: 6000,
  success: 6000,
  warning: 8000,
  error: 10000,
  critical: null,
};

const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>();
const autoDismissTimeouts = new Map<string, ReturnType<typeof setTimeout>>();

type Action =
  | { type: "ADD_TOAST"; toast: ToastInternal }
  | { type: "UPDATE_TOAST"; toast: Partial<ToastInternal> & { id: string } }
  | { type: "DISMISS_TOAST"; toastId?: string }
  | { type: "REMOVE_TOAST"; toastId?: string };

const listeners: Array<(state: ToastState) => void> = [];

let memoryState: ToastState = { toasts: [] };
let soundHandler: ((tone: ToastTone) => void) | null = null;
let count = 0;

const generateId = () => {
  count = (count + 1) % Number.MAX_SAFE_INTEGER;
  return `toast-${count}`;
};

const resolveDuration = (tone: ToastTone, explicit?: number | null): {
  durationMs: number;
  autoDismiss: boolean;
} => {
  if (explicit === 0 || explicit === null) {
    return { durationMs: PERSIST_DURATION, autoDismiss: false };
  }
  if (typeof explicit === "number") {
    return {
      durationMs: explicit <= 0 ? PERSIST_DURATION : explicit,
      autoDismiss: explicit > 0,
    };
  }
  const base = DEFAULT_DURATIONS[tone];
  if (base === null) {
    return { durationMs: PERSIST_DURATION, autoDismiss: false };
  }
  return {
    durationMs: base,
    autoDismiss: base > 0,
  };
};

const clearAutoDismiss = (toastId: string) => {
  const timeout = autoDismissTimeouts.get(toastId);
  if (timeout) {
    clearTimeout(timeout);
    autoDismissTimeouts.delete(toastId);
  }
};

const scheduleAutoDismiss = (toast: ToastInternal) => {
  clearAutoDismiss(toast.id);
  if (!toast.autoDismiss || toast.durationMs >= PERSIST_DURATION || toast.durationMs <= 0) {
    return;
  }
  const timeout = setTimeout(() => {
    autoDismissTimeouts.delete(toast.id);
    dispatch({ type: "DISMISS_TOAST", toastId: toast.id });
  }, toast.durationMs);
  autoDismissTimeouts.set(toast.id, timeout);
};

const cleanupStaleTimers = (state: ToastState) => {
  const activeToastIds = new Set(state.toasts.map((toast) => toast.id));

  for (const [toastId, timeout] of Array.from(autoDismissTimeouts.entries())) {
    if (!activeToastIds.has(toastId)) {
      clearTimeout(timeout);
      autoDismissTimeouts.delete(toastId);
    }
  }

  for (const [toastId, timeout] of Array.from(toastTimeouts.entries())) {
    if (!activeToastIds.has(toastId)) {
      clearTimeout(timeout);
      toastTimeouts.delete(toastId);
    }
  }
};

const dispatch = (action: Action) => {
  memoryState = toastReducer(memoryState, action);

  switch (action.type) {
    case "ADD_TOAST": {
      const toast = memoryState.toasts.find((item) => item.id === action.toast.id);
      if (toast) {
        scheduleAutoDismiss(toast);
      }
      break;
    }
    case "UPDATE_TOAST": {
      const toast = memoryState.toasts.find((item) => item.id === action.toast.id);
      if (toast) {
        if (toast.open) {
          scheduleAutoDismiss(toast);
        } else {
          clearAutoDismiss(toast.id);
        }
      }
      break;
    }
    case "DISMISS_TOAST": {
      if (action.toastId) {
        clearAutoDismiss(action.toastId);
      } else {
        for (const toast of memoryState.toasts) {
          clearAutoDismiss(toast.id);
        }
      }
      break;
    }
    case "REMOVE_TOAST": {
      if (action.toastId) {
        clearAutoDismiss(action.toastId);
      } else {
        for (const toastId of Array.from(autoDismissTimeouts.keys())) {
          clearAutoDismiss(toastId);
        }
      }
      break;
    }
  }

  cleanupStaleTimers(memoryState);
  listeners.forEach((listener) => listener(memoryState));
};

const scheduleRemoval = (toastId: string) => {
  if (toastTimeouts.has(toastId)) {
    return;
  }
  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId);
    dispatch({ type: "REMOVE_TOAST", toastId });
  }, TOAST_REMOVE_DELAY);
  toastTimeouts.set(toastId, timeout);
};

function toastReducer(state: ToastState, action: Action): ToastState {
  switch (action.type) {
    case "ADD_TOAST": {
      const toasts = [action.toast, ...state.toasts].slice(0, TOAST_STACK_LIMIT);
      return { ...state, toasts };
    }
    case "UPDATE_TOAST": {
      return {
        ...state,
        toasts: state.toasts.map((toast) =>
          toast.id === action.toast.id ? { ...toast, ...action.toast } : toast,
        ),
      };
    }
    case "DISMISS_TOAST": {
      const toastId = action.toastId;
      if (toastId) {
        scheduleRemoval(toastId);
      } else {
        state.toasts.forEach((toast) => {
          scheduleRemoval(toast.id);
        });
      }
      return {
        ...state,
        toasts: state.toasts.map((toast) =>
          toastId === undefined || toast.id === toastId
            ? { ...toast, open: false }
            : toast,
        ),
      };
    }
    case "REMOVE_TOAST": {
      if (action.toastId === undefined) {
        return { ...state, toasts: [] };
      }
      return {
        ...state,
        toasts: state.toasts.filter((toast) => toast.id !== action.toastId),
      };
    }
    default:
      return state;
  }
}

export const toast = (options: ToastOptions = {}) => {
  const id = options.id ?? generateId();
  const tone = options.tone ?? "info";
  const { durationMs, autoDismiss } = resolveDuration(tone, options.duration);

  const toastInternal: ToastInternal = {
    ...options,
    id,
    tone,
    open: true,
    createdAt: Date.now(),
    dismissible: options.dismissible ?? true,
    durationMs,
    autoDismiss,
    progress:
      typeof options.progress === "number" ? clampProgress(options.progress) : null,
  };

  dispatch({ type: "ADD_TOAST", toast: toastInternal });

  if ((options.sound ?? true) && soundHandler) {
    soundHandler(tone);
  }

  return {
    id,
    dismiss: () => dispatch({ type: "DISMISS_TOAST", toastId: id }),
    update: (next: ToastOptions) => {
      const partial: Partial<ToastInternal> & { id: string } = {
        id,
        ...next,
      };
      if (next.tone) {
        const resolved = resolveDuration(next.tone, next.duration);
        partial.durationMs = resolved.durationMs;
        partial.autoDismiss = resolved.autoDismiss;
      } else if (next.duration !== undefined) {
        const resolved = resolveDuration(tone, next.duration);
        partial.durationMs = resolved.durationMs;
        partial.autoDismiss = resolved.autoDismiss;
      }
      if (next.progress !== undefined) {
        partial.progress =
          next.progress === null
            ? null
            : clampProgress(next.progress);
      }
      dispatch({ type: "UPDATE_TOAST", toast: partial });
    },
  };
};

export const dismissToast = (toastId?: string) => {
  dispatch({ type: "DISMISS_TOAST", toastId });
};

const clampProgress = (value: number) => {
  if (!Number.isFinite(value)) {
    return null;
  }
  return Math.min(1, Math.max(0, value));
};

export const useToast = () => {
  const [state, setState] = React.useState<ToastState>(memoryState);

  React.useEffect(() => {
    listeners.push(setState);
    return () => {
      const index = listeners.indexOf(setState);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    };
  }, []);

  const registerSound = React.useCallback(
    (handler: ((tone: ToastTone) => void) | null) => {
      soundHandler = handler;
      return () => {
        if (soundHandler === handler) {
          soundHandler = null;
        }
      };
    },
    [],
  );

  return {
    ...state,
    toast,
    dismiss: dismissToast,
    dismissAll: () => dismissToast(),
    registerSound,
  };
};

export type { ToastInternal as ToastRecord };
